import { RefObject, useRef } from 'react';

import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryOptions, QueryRefetchInterval } from '@cast/shared-types';
import { KentClusterOverview } from '@cast/types-kent';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { mapKentOverviewDtoToKentOverview } from 'features/kent/utils';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

const KENT_CLUSTER_OVERVIEW_QUERY_KEY = 'kent-cluster-overview';

export const getClusterOverviewQueryKey = (clusterId: string) =>
  [KENT_CLUSTER_OVERVIEW_QUERY_KEY, clusterId] as const;

const getClusterOverviewQueryOptions = ({
  organizationId,
  clusterId,
  invalidateServerMetricsCache,
  enabled,
  staleTime,
  refetchInterval,
}: {
  organizationId?: string;
  clusterId: string;
  invalidateServerMetricsCache: RefObject<boolean>;
  enabled?: boolean;
  staleTime?: QueryOptions<KentClusterOverview>['staleTime'];
  refetchInterval?: QueryRefetchInterval<KentClusterOverview>;
}) =>
  queryOptions({
    queryKey: getClusterOverviewQueryKey(clusterId),
    queryFn: async () => {
      try {
        const { data } = await apiClient.kent.getClusterOverview({
          organizationId: organizationId!,
          clusterId,
          invalidateCache: invalidateServerMetricsCache.current,
        });
        return mapKentOverviewDtoToKentOverview(data);
      } finally {
        invalidateServerMetricsCache.current = false;
      }
    },
    enabled: makeIsEnabled(!!clusterId, enabled),
    staleTime,
    refetchInterval,
  });

type GetClusterOverviewQueryArgs = QueryOptions<KentClusterOverview> & {
  clusterId: string;
};

export const useGetClusterOverviewQuery = ({
  clusterId,
  enabled,
  staleTime,
  refetchInterval,
}: GetClusterOverviewQueryArgs) => {
  const { currentOrganization } = useOrganizations();
  const invalidateServerMetricsCache = useRef(false);

  const query = useQuery(
    getClusterOverviewQueryOptions({
      organizationId: currentOrganization?.id,
      clusterId,
      invalidateServerMetricsCache,
      enabled,
      staleTime,
      refetchInterval,
    })
  );

  return {
    ...query,
    refetchWithServerMetricsCacheInvalidation: () => {
      invalidateServerMetricsCache.current = true;
      return query.refetch();
    },
  };
};
